<script lang="ts">
  import { getContext } from "svelte";
  import Button from "../../../../common/form/Button.svelte";
  import Icon from "../../../../common/icons/Icon.svelte";
  import HorizontalDivider from "../../../common/HorizontalDivider.svelte";
  import FormInput from "../../../../common/form/FormInputWithSurroundError.svelte";
  import AuthFormStyle from "../../../common/AuthFormStyle.svelte";
  import SignUpNote from "./SignUpNote.svelte";
  import Link from "../../../../common/link/Link.svelte";
  import PasswordStrengthIndicator from "./PasswordStrengthIndicator.svelte";
  import type { PasswordStrength, CreateNewAccountContext } from "../../type";
  import {
    checkPasswordStrength,
    CREATE_NEW_ACCOUNT_CONTEXT_NAME,
  } from "../../utils";
  import pageUrl from "../../../../../PageUrl";
  import * as yup from "yup";
  import { validator } from "@felte/validator-yup";
  import { createForm, getValue } from "felte";
  import type { APIError } from "../../../../../app/api";
  import { register, signIn, signInWithGoogle } from "../../../../../app/api";
  import { userInfoStore } from "../../../../../app/store/store-user";

  const context: CreateNewAccountContext = getContext(
    CREATE_NEW_ACCOUNT_CONTEXT_NAME
  );
  let canSubmit: boolean = true;
  let isSubmitting: boolean = false;
  let serverError: string = "";
  // first name
  let firstNameErrorMsg: string = "";
  let firstName: string = context.getFirstName();
  // last name
  let lastNameErrorMsg: string = "";
  let lastName: string = context.getLastName();
  // email
  let email: string = context.getEmail();
  let submittedEmail: string = null;
  let emailErrorMsg: string = "";
  let isEmailFocus: boolean = false;
  // password
  const minPasswordLength = 6;
  let passwordErrorMsg: string = "";
  let passwordStrength: PasswordStrength = "";
  let isPasswordFocus: boolean = false;
  let isPasswordVisible = false;
  // should only auto go to next page after success fully add user information
  // it prevent auto go to next page when user navigate using other method
  // such as progress bar at top or from link
  let shouldAutoGoToNextPage: boolean = false;

  function togglePasswordVisible() {
    isPasswordVisible = !isPasswordVisible;
  }

  function handlePasswordValueChange(e: InputEvent) {
    const target = e.target as HTMLInputElement;
    const passwordValue = target.value;
    passwordStrength =
      passwordValue && passwordValue.length >= minPasswordLength
        ? checkPasswordStrength(passwordValue)
        : "";
  }

  const schema = yup.object({
    firstname: yup
      .string()
      .test(
        "required",
        "Please enter your first name",
        (value) => !!value.trim()
      ),
    lastname: yup
      .string()
      .test(
        "required",
        "Please enter your last name",
        (value) => !!value.trim()
      ),
    email: yup
      .string()
      .email("Invalid email address")
      .required("Please enter your email"),
    password: yup
      .string()
      .min(minPasswordLength, "That password is too short")
      .required("Please enter your password"),
  });

  function updateErrorMessage() {
    // if there is current user it's mean the user information has been store
    // no need to check for error.
    if (currentUser) {
      firstNameErrorMsg =
        lastNameErrorMsg =
        passwordErrorMsg =
        emailErrorMsg =
          "";
      submittedEmail = null;
      canSubmit = true;
      return;
    }

    let errs = $errors;
    const getError = (er: any[]) => (er ? er[0] : "");

    if (!submittedEmail || submittedEmail !== email) {
      emailErrorMsg = getError(errs.email);
      submittedEmail = null;
    }
    firstNameErrorMsg = getError(errs.firstname);
    lastNameErrorMsg = getError(errs.lastname);
    passwordErrorMsg = getError(errs.password);
    canSubmit = [
      firstNameErrorMsg,
      lastNameErrorMsg,
      emailErrorMsg,
      passwordErrorMsg,
    ].every((v) => !v);
  }

  let { form, errors, validate } = createForm({
    extend: validator({ schema }),
    onSubmit: async (values) => {
      // Go to next form without doing anything because the user information
      // has been stored on server
      if (currentUser) {
        context.onNext({});
        return;
      }

      if (isSubmitting) return;
      await validate();
      updateErrorMessage();
      if (!canSubmit) return;
      isSubmitting = true;
      const { firstname, lastname, email, password } = values;
      let isSuccess: boolean;
      await register(
        email.trim(),
        password,
        firstname.trim(),
        lastname.trim(),
        {
          onSuccess: async () => {
            isSuccess = true;
          },
          onError: async (error: APIError) => {
            serverError = "";
            if (error.key === "email") {
              submittedEmail = email;
              emailErrorMsg = error.message;
            } else {
              serverError = error.message;
            }
            updateErrorMessage();
          },
        }
      );
      if (isSuccess) {
        await signIn(email.trim(), password);
        shouldAutoGoToNextPage = true;
      }
      isSubmitting = false;
    },
  });

  function onContinueButtonClick() {
    if (currentUser) context.onNext({});
  }

  async function handleSignInWithGoogle() {
    if (isSubmitting) return;
    isSubmitting = true;
    try {
      await signInWithGoogle();
    } catch (error) {}
    isSubmitting = false;
  }

  async function handleFormSubmit() {
    await validate();
    updateErrorMessage();
  }

  // $: $errors, updateErrorMessage();

  $: currentUser = $userInfoStore;
  $: if (currentUser) {
    email = currentUser.email;
    firstName = currentUser.firstName;
    lastName = currentUser.lastName;
    context.updatePersonalInforFromUserData();
    if (shouldAutoGoToNextPage) {
      context.onNext({});
    }
  }
</script>

<form use:form method="POST" on:submit|capture={handleFormSubmit}>
  <div
    class="google-account mt-6 mb-2 flex flex-row items-center justify-center"
  >
    <Button
      disabled={isSubmitting}
      color="secondary"
      type="button"
      on:click={handleSignInWithGoogle}
    >
      <Icon icon="google" size="20px" />
      <p>Continue with Google</p>
    </Button>
  </div>

  <HorizontalDivider>OR</HorizontalDivider>

  <div class="input-name">
    <div class="input-field flex-grow">
      <p class="required">First Name</p>
      <div class="input-container input-only">
        <FormInput
          name="firstname"
          bind:value={firstName}
          errorMessage={firstNameErrorMsg}
          errorIndicatorIconSize="20px"
          type="text"
          spellcheck="false"
          placeholder="e.g. John"
          autocomplete="off"
          disabled={isSubmitting}
        />
      </div>
    </div>

    <div class="input-field flex-grow">
      <p class="required">Last Name</p>
      <div class="input-container input-only">
        <FormInput
          name="lastname"
          bind:value={lastName}
          errorMessage={lastNameErrorMsg}
          errorIndicatorIconSize="20px"
          type="text"
          spellcheck="false"
          placeholder="e.g. Doe"
          autocomplete="off"
          disabled={isSubmitting}
        />
      </div>
    </div>
  </div>

  <div class="input-field mt-7">
    <p class="required">Email Address</p>
    <div class="input-container" class:focus={isEmailFocus}>
      <div class="prefix-icon">
        <Icon icon="input-email" width="20px" height="18px" />
      </div>
      <FormInput
        name="email"
        bind:value={email}
        errorMessage={emailErrorMsg}
        bind:isFocus={isEmailFocus}
        errorIndicatorIconSize="20px"
        type="email"
        spellcheck="false"
        placeholder="Enter Your Email"
        autocomplete="off"
        disabled={isSubmitting}
      />
    </div>
  </div>

  <div class="input-field">
    <div class="flex flex-row items-center justify-between">
      <p class="required">Password</p>
      <div class="mb-1.5">
        <PasswordStrengthIndicator strength={passwordStrength} />
      </div>
    </div>
    <div class="input-container" class:focus={isPasswordFocus}>
      <div class="prefix-icon">
        <Icon icon="input-password" width="20px" height="20px" />
      </div>

      <FormInput
        name="password"
        value={context.getPassword()}
        errorMessage={passwordErrorMsg}
        on:input={handlePasswordValueChange}
        bind:isFocus={isPasswordFocus}
        showErrorIndicatorIcon={false}
        type={isPasswordVisible ? "text" : "password"}
        spellcheck="false"
        placeholder="Enter Your Password"
        autocomplete="off"
        disabled={isSubmitting}
      />

      <div class="postfix-icon">
        <button type="button" class="w-5 h-5" on:click={togglePasswordVisible}>
          <Icon
            width="20px"
            height="20px"
            icon={isPasswordVisible ? "password-visible" : "password-invisible"}
          />
        </button>
      </div>
    </div>
  </div>

  <div class="mt-9">
    {#if serverError}
      <div class="pb-2 text-tertiary-error font-medium text-center">
        {serverError}
      </div>
    {/if}
    <SignUpNote />
  </div>

  <div class="submit-button">
    <Button
      on:click={onContinueButtonClick}
      class="w-full mt-3 py-3 px-0"
      disabled={isSubmitting}
    >
      Continue
    </Button>
  </div>

  <HorizontalDivider class="font-medium mt-4">
    <div>
      <span>Already have an acount?</span>
      <Link href={pageUrl.SIGNIN} class="font-semibold">Sign In Now</Link>
    </div>
  </HorizontalDivider>
</form>

<AuthFormStyle />

<style lang="postcss">
  .google-account :global(button.secondary) {
    @apply w-full flex flex-row items-center justify-center py-3 px-0 gap-2.5 text-dark-200;
    font-size: 13px;
  }

  .input-container.input-only :global(input) {
    padding: 14px 44px 14px 16px !important;
  }

  .input-field .input-container :global(input::placeholder) {
    @apply font-light;
  }

  .input-name {
    @apply flex flex-row items-center gap-4 mt-4;
  }

  .input-name .input-field + .input-field {
    @apply mt-0;
  }

  @media (max-width: 600px) {
    .input-name {
      @apply flex-col gap-7;
    }

    .input-name .input-field {
      @apply w-full;
    }
  }
</style>
